import { useEffect, useState } from 'react'
import { client } from '@common/config/api/client'
import type { TimepointSubmissionUploader } from '@common/types/uploader'

interface UploaderParams {
  study_id?: string
  subject_id?: string
  timepoint_submission_id?: string
}

/**
 * Read-only lookup of the user who uploaded/submitted the timepoint data (Subjects flow).
 * Only fetches when `enabled` is true (e.g. when the details popup is opened).
 */
export const useGetTimepointSubmissionUploader = (params: UploaderParams, enabled: boolean) => {
  const [data, setData] = useState<TimepointSubmissionUploader>()
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    const canFetch = enabled && !!params.study_id && !!params.subject_id && !!params.timepoint_submission_id

    if (!canFetch) {
      return
    }

    let isCurrentRequest = true
    setIsLoading(true)
    setIsError(false)

    client
      .get<TimepointSubmissionUploader, unknown, true>({
        url: '/studies/{study_id}/subjects/{subject_id}/timepoint_submission/{timepoint_submission_id}/uploader',
        path: {
          study_id: params.study_id!,
          subject_id: params.subject_id!,
          timepoint_submission_id: params.timepoint_submission_id!,
        },
        throwOnError: true,
      })
      .then((response) => {
        if (isCurrentRequest) {
          setData(response.data as TimepointSubmissionUploader)
        }
      })
      .catch(() => {
        if (isCurrentRequest) {
          setIsError(true)
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoading(false)
        }
      })

    return () => {
      isCurrentRequest = false
    }
  }, [enabled, params.study_id, params.subject_id, params.timepoint_submission_id])

  return { data, isLoading, isError }
}
